import { getSupabase } from '@/lib/supabase';
import { decodeRowToSessionIndex, type SessionIndexRow } from '@/lib/sync/codec';
import type { SessionIndexEntry } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import type { RealtimeSubscription } from '../realtime';

interface SessionIndexChange {
  new: SessionIndexRow;
}

export async function subscribeToSessionIndex(
  userId: UserId,
  onEntry: (entry: SessionIndexEntry) => void,
): Promise<RealtimeSubscription> {
  const userIdValue = String(userId);
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();

  if (!session?.user) {
    throw new Error('User must be authenticated to subscribe to session index');
  }

  const channel = client.channel(`session-index:${userIdValue}`);

  const typedChannel = channel as unknown as {
    on: (
      type: 'postgres_changes',
      config: {
        event: 'INSERT' | 'UPDATE';
        schema: 'public';
        table: 'session_index';
        filter: string;
      },
      callback: (payload: SessionIndexChange) => void,
    ) => typeof channel;
  };

  const subscribe = (event: 'INSERT' | 'UPDATE') => {
    typedChannel.on(
      'postgres_changes',
      {
        event,
        schema: 'public',
        table: 'session_index',
        filter: `user_id=eq.${userIdValue}`,
      },
      (payload: SessionIndexChange) => {
        try {
          const entry = decodeRowToSessionIndex(payload.new);
          onEntry(entry);
        } catch (error) {
          console.warn('Failed to decode session index change', error);
        }
      },
    );
  };

  subscribe('INSERT');
  subscribe('UPDATE');

  channel.subscribe();

  return {
    unsubscribe: () => {
      void client.removeChannel(channel);
    },
  };
}
